import { useState } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Clock, Bot, User, PhoneIcon } from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { okaidia } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import useCall from '@/hooks/useCall';
import Drawer from '../Modals/Drawer';
import Phone from '../Phone';
import Time from '../Time';
import Transcript from './Transcript';

interface DrawerProps {
  callId?: string;
}

const formatDuration = (seconds: number): string => {
  if (!seconds) return '0:00';

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const CallDrawer = NiceModal.create(({ callId }: DrawerProps) => {
  const modal = useModal('call-drawer');
  const call = useCall(callId);

  const callData = call?.data;
  const [activeTab, setActiveTab] = useState('overview');

  const renderContent = () => {
    if (!callData) {
      return (
        <Card>
          <CardContent className="p-6">
            <p>No call data available.</p>
          </CardContent>
        </Card>
      );
    }

    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Call Overview</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4">
                <div className="flex items-center gap-2">
                  <PhoneIcon className="w-4 h-4" />
                  <span>
                    From: <Phone>{callData.from}</Phone>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <PhoneIcon className="w-4 h-4" />
                  <span>
                    To: <Phone>{callData.to}</Phone>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>Duration: {formatDuration(callData.corrected_duration || callData.duration)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={callData.status === 'completed' ? 'default' : 'secondary'}>
                    {callData.status || 'Unknown'}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {callData.summary && (
              <Card>
                <CardHeader>
                  <CardTitle>Summary</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4">{callData.summary || 'N/A'}</CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Participants</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4">
                <div className="flex items-center gap-4">
                  <Avatar>
                    <AvatarFallback>
                      <User />
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{callData.contact?.name || 'Customer'}</p>
                    <p className="text-sm text-muted-foreground">
                      <Phone>{callData.metadata?.call_direction === 'inbound' ? callData.from : callData.to}</Phone>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Avatar>
                    <AvatarFallback>{callData.call_type === 'external' ? <User /> : <Bot />}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">
                      {callData.call_type === 'external' ? callData.metadata?.agent_name || 'Agent' : 'Bot'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <Phone>{callData.metadata?.call_direction === 'inbound' ? callData.to : callData.from}</Phone>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Call Details</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4">
                {callData.recording_url && (
                  <div className="mt-4">
                    <p className="mb-2 font-medium">Recording</p>
                    <audio controls src={callData.audio_file_url} className="w-full">
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="font-medium">Started At</p>
                    <p className="text-sm text-muted-foreground">
                      <Time>{callData.call_started_at}</Time>
                    </p>
                  </div>
                  <div>
                    <p className="font-medium">Ended At</p>
                    <p className="text-sm text-muted-foreground">
                      <Time>{callData.call_ended_at}</Time>
                    </p>
                  </div>
                  <div>
                    <p className="font-medium">Ended By</p>
                    <p className="text-sm text-muted-foreground">{callData.call_ended_by || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-medium">Queue Status</p>
                    <p className="text-sm text-muted-foreground">{callData.status || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-medium">Call ID</p>
                    <p className="text-sm text-muted-foreground">{callData.id || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-medium">Price</p>
                    <p className="text-sm text-muted-foreground">${callData.cost}</p>
                  </div>
                  {callData.call_type !== 'external' && (
                    <div>
                      <p className="font-medium">Disposition</p>
                      <p className="text-sm text-muted-foreground">${callData.disposition_tag}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'transcript': {
        return <Transcript call={callData} />;
      }
      case 'metadata': {
        return (
          <Card>
            <CardHeader>
              <CardTitle>Metadata</CardTitle>
            </CardHeader>
            <CardContent>
              {callData.metadata ? (
                <SyntaxHighlighter
                  language="text"
                  style={okaidia}
                  customStyle={{
                    borderRadius: '8px',
                    padding: '16px',
                    fontSize: '14px',
                    overflowY: 'auto',
                  }}
                >
                  {JSON.stringify(callData.metadata, null, 2)}
                </SyntaxHighlighter>
              ) : (
                <p>No metadata available.</p>
              )}
            </CardContent>
          </Card>
        );
      }
      case 'variables': {
        return (
          <Card>
            <CardHeader>
              <CardTitle>Variables</CardTitle>
            </CardHeader>
            <CardContent>
              {callData.variables ? (
                <SyntaxHighlighter
                  language="text"
                  style={okaidia}
                  customStyle={{
                    borderRadius: '8px',
                    padding: '16px',
                    fontSize: '14px',
                    overflowY: 'auto',
                  }}
                >
                  {JSON.stringify(callData.variables, null, 2)}
                </SyntaxHighlighter>
              ) : (
                <p>No variables available.</p>
              )}
            </CardContent>
          </Card>
        );
      }
      case 'pathway': {
        return (
          <Card>
            <CardHeader>
              <CardTitle>Pathway Logs</CardTitle>
            </CardHeader>
            <CardContent>
              {callData.pathway_logs ? (
                <SyntaxHighlighter
                  language="text"
                  style={okaidia}
                  customStyle={{
                    borderRadius: '8px',
                    padding: '16px',
                    fontSize: '14px',
                    overflowY: 'auto',
                  }}
                >
                  {JSON.stringify(callData.pathway_logs, null, 2)}
                </SyntaxHighlighter>
              ) : (
                <p>No pathway logs available.</p>
              )}
            </CardContent>
          </Card>
        );
      }
      case 'full': {
        return (
          <Card>
            <CardHeader>
              <CardTitle>Complete Call Log</CardTitle>
            </CardHeader>
            <CardContent>
              <SyntaxHighlighter
                language="text"
                style={okaidia}
                customStyle={{
                  borderRadius: '8px',
                  padding: '16px',
                  fontSize: '14px',
                  overflowY: 'auto',
                }}
              >
                {JSON.stringify(callData, null, 2)}
              </SyntaxHighlighter>
            </CardContent>
          </Card>
        );
      }
      default:
        return null;
    }
  };

  return (
    <Drawer modal={modal} title="Call Details">
      <div className="space-y-6">
        <div className="flex mb-4 space-x-2 overflow-x-auto">
          <Button variant={activeTab === 'overview' ? 'default' : 'outline'} onClick={() => setActiveTab('overview')}>
            Overview
          </Button>

          <Button
            variant={activeTab === 'transcript' ? 'default' : 'outline'}
            onClick={() => setActiveTab('transcript')}
          >
            Transcript
          </Button>
          <Button variant={activeTab === 'metadata' ? 'default' : 'outline'} onClick={() => setActiveTab('metadata')}>
            Metadata
          </Button>
          <Button variant={activeTab === 'variables' ? 'default' : 'outline'} onClick={() => setActiveTab('variables')}>
            Variables
          </Button>
          <Button variant={activeTab === 'pathway' ? 'default' : 'outline'} onClick={() => setActiveTab('pathway')}>
            Pathway Logs
          </Button>
          <Button variant={activeTab === 'full' ? 'default' : 'outline'} onClick={() => setActiveTab('full')}>
            Full Log
          </Button>
        </div>
        <div className="overflow-y-auto max-h-[calc(100vh-200px)]">{renderContent()}</div>
      </div>
    </Drawer>
  );
});

export default CallDrawer;
